import React, { useState } from 'react';
import { useUpdateProgressTrackerSettings } from '../hooks/progressTracker/useUpdateProgressTrackerSettings';
import { progressTrackerQueryKeys } from '../hooks/progressTracker/progressTrackerQueryKeys';
import { ProgressTrackerSettingItemProps } from '../ProgressTrackerSettingItem';
import { ProgressTrackerSettingsContext, ProgressTrackerSettingsContextType } from './ProgressTrackerSettings.context';
import { TimelineChartOrientation } from '@opengov/stories-shared/types';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchProgressTrackerSettings } from '../hooks/progressTracker/useFetchProgressTrackerSettings';
import { useToast } from '../../../Toast/context/useToast.context';

export enum VerticalLabelPosition {
  Left = 'left',
  Right = 'right',
}
export enum HorizontalLabelPosition {
  Top = 'top',
  Bottom = 'bottom',
}
export const ProgressTrackerSettingsProvider = ({
  children,
  entityId
}: {
  children: React.ReactNode;
  entityId: number;
}) => {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();
  const { data, isLoading, isError } = useFetchProgressTrackerSettings(entityId);
  const { mutate: updateSettings, isLoading: isDataUpdating } = useUpdateProgressTrackerSettings(entityId);
  const [localSettings, setLocalSettings] = useState<ProgressTrackerSettingItemProps[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  const [activeSettingId, setActiveSettingId] = useState<string>('');
  const [currentAlignment, setCurrentAlignment] = useState<TimelineChartOrientation>(TimelineChartOrientation.HORIZONTAL);

  React.useEffect(() => {
    if (isError) {
      toggleToast({
        title: 'Failed to load progress tracker settings',
        severity: 'error'
      });
      return;
    }
    if ((data && localSettings.length === 0)) {
      const initialSettings: ProgressTrackerSettingItemProps[] = data.map((setting) => ({
        entityId: setting.entityId,
        id: setting.id,
        status: setting.status,
        colorCode: setting.colorCode,
        verticalLabelPosition: setting.verticalLabelPosition,
        horizontalLabelPosition: setting.horizontalLabelPosition
      }));
      setLocalSettings(initialSettings);
      setHasChanges(false);
    }
    else {
      const updatedSettings = localSettings.map((setting) => {
        return {
          ...setting,
          verticalLabelPosition: setting.verticalLabelPosition,
          horizontalLabelPosition: setting.horizontalLabelPosition
        }
      })
      setLocalSettings(updatedSettings);
    }
  }, [data, currentAlignment, isError]);
  const updateLocalProgressTrackerSettings = (settings: ProgressTrackerSettingItemProps) => {
    setLocalSettings((prev) => {
      const updated = [...prev];
      updated[updated.findIndex((s) => s.id === settings.id)] = settings;
      return updated;
    });
    setHasChanges(true);
  };
  const addNewStatus = () => {
    setLocalSettings((prev) => {
      const updated = [...prev];
      updated.push({ id: `New-${Date.now()}`, status: '', colorCode: '#616365' });
      return updated;
    });
    setHasChanges(true);
  };

  const resetLocalProgressTrackerSettings = () => {
    if (!data) return;
    const initialSettings: ProgressTrackerSettingItemProps[] = data?.map((setting) => ({
      entityId: setting.entityId || 0,
      id: setting.id || '',
      status: setting.status || '',
      colorCode: setting.colorCode || '',
      verticalLabelPosition: setting.verticalLabelPosition,
      horizontalLabelPosition: setting.horizontalLabelPosition,
    }));
    setLocalSettings(initialSettings);
    setHasChanges(false);
  };
  const validateLocalSettings = () => {
    const emptySetting = localSettings.find((setting) => setting.status === '' || setting.colorCode === '');
    if (emptySetting) {
      toggleToast({
        title: 'Status and color code are required for all settings',
        severity: 'error'
      });
      return false;
    }
    const statusArray = localSettings.map((setting) => setting.status?.toLowerCase().trim() || '');
    const isDuplicateStatus = new Set(statusArray).size !== statusArray.length;
    if (isDuplicateStatus) {
      toggleToast({
        title: 'Status must be unique',
        severity: 'error'
      });
      return false;
    }
    const isVerticalLabelPositionInvalid = localSettings.some((setting) => !setting.verticalLabelPosition);
    const isHoizontalLabelPositionInvalid = localSettings.some((setting) => !setting.horizontalLabelPosition);
    if (isVerticalLabelPositionInvalid || isHoizontalLabelPositionInvalid) {
      toggleToast({
        title: 'Vertical and horizontal label position are required for all settings',
        severity: 'error'
      });
      return false;
    }
    return true;
  }
  const saveProgressTrackerSettings = () => {
    if (!validateLocalSettings()) {
      return;
    }
    const progressTrackerRequestData = {
      entityId: entityId,
      settings: localSettings.map((setting: ProgressTrackerSettingItemProps) => {
        return {
          id: setting.id && setting.id.includes('New-') ? '' : setting.id,
          status: setting.status || '',
          colorCode: setting.colorCode || '',
          entityId: entityId,
          verticalLabelPosition: setting.verticalLabelPosition,
          horizontalLabelPosition: setting.horizontalLabelPosition
        };
      })
    };

    updateSettings(progressTrackerRequestData, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: progressTrackerQueryKeys.settings(entityId) });
        setHasChanges(false);
        toggleToast({
          title: 'Changes saved',
          severity: 'success'
        });
      },
      onError: (error: Error) => {
        let errorMessage = 'An error occurred while saving settings';
        if (error?.message) {
          errorMessage = error.message;
        }
        toggleToast({
          title: errorMessage,
          severity: 'error'
        });
      },
    });
  };

  const contextValue: ProgressTrackerSettingsContextType = {
    progressTrackerSettings: data || [],
    localProgressTrackerSettings: localSettings,
    updateLocalProgressTrackerSettings,
    resetLocalProgressTrackerSettings,
    saveProgressTrackerSettings,
    hasChanges,
    showColorModal,
    setShowColorModal,
    activeSettingId,
    setActiveSettingId,
    currentAlignment,
    setCurrentAlignment,
    addNewStatus,
    isLoading,
    isDataUpdating
  };

  return (
    <ProgressTrackerSettingsContext.Provider value={contextValue}>{children}</ProgressTrackerSettingsContext.Provider>
  );
};
